import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AppLanguage, TranslationService } from '@core';
import { SzBrandBlockComponent, SzIconComponent } from '@ui';

interface LanguageOption {
  readonly code: string;
  readonly appCode: AppLanguage | null;
  readonly label: string;
  readonly native: string;
}

@Component({
  selector: 'app-language-picker-page',
  templateUrl: './language-picker.page.html',
  styleUrls: ['./language-picker.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslateModule, SzBrandBlockComponent, SzIconComponent]
})
export class LanguagePickerPage {
  readonly options: ReadonlyArray<LanguageOption> = [
    { code: 'en', appCode: AppLanguage.en, label: 'English', native: 'English' },
    { code: 'pl', appCode: AppLanguage.pl, label: 'Polski', native: 'Polish' },
    { code: 'de', appCode: AppLanguage.de, label: 'Deutsch', native: 'German' },
    { code: 'cs', appCode: AppLanguage.cs, label: 'Čeština', native: 'Czech' },
    { code: 'fr', appCode: AppLanguage.fr, label: 'Français', native: 'French' }
  ];

  private readonly translation: TranslationService = inject(TranslationService);
  private readonly router: Router = inject(Router);

  readonly selected: WritableSignal<AppLanguage> = signal<AppLanguage>(this.translation.currentLanguage());

  readonly selectedLabel: Signal<string> = computed((): string => {
    const sel: AppLanguage = this.selected();
    const opt: LanguageOption | undefined = this.options.find((o: LanguageOption): boolean => o.appCode === sel);
    return opt?.label ?? '';
  });

  pick(opt: LanguageOption): void {
    if (opt.appCode === null) return;
    this.selected.set(opt.appCode);
    // apply preview immediately so headline switches as user picks; switchLanguage
    // also persists to localStorage which marks first-launch as done - fine
    // because picking IS the user's explicit choice
    this.translation.switchLanguage(opt.appCode);
  }

  isSelected(opt: LanguageOption): boolean {
    return opt.appCode !== null && opt.appCode === this.selected();
  }

  confirm(): void {
    this.translation.switchLanguage(this.selected());
    void this.router.navigateByUrl('/');
  }
}
